import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { paymentReceiptService } from "../services/paymentReceiptService";
import { paymentReceiptDisplayId } from "./PaymentReceiptCommonStore";
import { PaymentReceipt } from "../../../api/graphql";
import { paymentReceiptTypeDisplayId } from "../../paymentReceiptType/store/PaymentReceiptTypeCommonStore";
import { signedEditionDisplayId } from "../../signedEdition/store/SignedEditionCommonStore";
import { getPaymentReceiptUseValues } from "../../paymentReceiptUse/store/PaymentReceiptUseCommonStore";
import { ColumnWidthInfo } from "../../common/services/crudServices";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { CrudRows } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { PaymentReceiptStatus } from "../../../api/graphql";
import { paymentReceiptTypeService } from "../../paymentReceiptType/services/paymentReceiptTypeService";
import { signedEditionService } from "../../signedEdition/services/signedEditionService";
import { InitializeParams } from "../../../store/types";

export class PaymentReceiptViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = paymentReceiptService;
  paymentReceiptInstance: PaymentReceipt | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  paymentReceiptFiles: CrudRows = {};
  crcValue: string = "";
  typeService = paymentReceiptTypeService;
  typeSearchValue: string = "";
  typeIdValue: string = "";
  typeSelectedValue: { displayId: string; id: string } | null = null;
  amountValue: number = 0;
  issueDateTimeValue: Date | null = null;
  statusValue: PaymentReceiptStatus = "" as PaymentReceiptStatus;
  totalUsesValue: number = 0;
  remainingUsesValue: number = 0;
  usedDateValue: Date | null = null;
  signedEditionService = signedEditionService;
  signedEditionSearchValue: string = "";
  signedEditionIdValue: string = "";
  signedEditionSelectedValue: { displayId: string; id: string } | null = null;
  usesColumnWidths: Record<string, number | string> = {};
  usesRows: CrudRows = {};
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;
    this.usesColumnWidths = { detail: 200, usedAt: 200 };

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadPaymentReceiptById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.paymentReceiptOne({ id });
    this.paymentReceiptInstance = result.paymentReceiptOne;
    if (this.paymentReceiptInstance) {
      this.loadValues(this.paymentReceiptInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.paymentReceiptInstance) {
      return paymentReceiptDisplayId(
        this.paymentReceiptInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PaymentReceiptSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "type") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.paymentReceiptTypeOne({
          id: params.returnData.resultId,
        });
        const paymentReceiptType = result.paymentReceiptTypeOne;
        this.typeSelectedValue = paymentReceiptType;
      } else if (params.callerData?.fieldName === "signedEdition") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.signedEditionOne({
          id: params.returnData.resultId,
        });
        const signedEdition = result.signedEditionOne;
        this.signedEditionSelectedValue = signedEdition;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get typeValue() {
    if (this.typeSelectedValue) {
      return {
        id: this.typeSelectedValue.id,
        displayId: paymentReceiptTypeDisplayId(
          this.typeSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  getStatusOptions() {
    return this.langStore.PaymentReceiptStatusOptions;
  }

  get signedEditionValue() {
    if (this.signedEditionSelectedValue) {
      return {
        id: this.signedEditionSelectedValue.id,
        displayId: signedEditionDisplayId(
          this.signedEditionSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  get usesVisibleColumns() {
    return [
      {
        name: "detail",
        title: this.langStore.PaymentReceiptUseDetailLabel,
        width: this.usesColumnWidths["detail"],
      },
      {
        name: "usedAt",
        title: this.langStore.PaymentReceiptUseUsedAtLabel,
        width: this.usesColumnWidths["usedAt"],
      },
    ];
  }

  usesSetColumnWiths(x: ColumnWidthInfo[]) {
    const values: Record<string, number | string> = {};
    for (const item of x) {
      values[item.columnName] = item.width;
    }
    this.usesColumnWidths = values;
  }

  get usesRowsDyn() {
    return this.usesRows;
  }

  *fileDownload(props: any, fieldName: string): any {
    yield downloadWithAuth({
      entityName: "PaymentReceipt",
      fieldName: fieldName,
      instanceId: this.paymentReceiptInstance?.id!,
      fileName: props.name,
    });
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      const blob = yield fetchFileBlob({
        entityName: "PaymentReceipt",
        fieldName: fieldName,
        instanceId: this.paymentReceiptInstance?.id!,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.paymentReceiptInstance = null;
    this.paymentReceiptFiles = {};
    this.crcValue = "";
    this.typeSearchValue = "";
    this.typeIdValue = "";
    this.amountValue = 0;
    this.issueDateTimeValue = null;
    this.statusValue = "" as PaymentReceiptStatus;
    this.totalUsesValue = 0;
    this.remainingUsesValue = 0;
    this.usedDateValue = null;
    this.signedEditionSearchValue = "";
    this.signedEditionIdValue = "";
    this.usesRows = {};
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelPaymentReceipt() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return this.rootStore.authStore.hasPermissions("paymentReceipt_update");
  }

  editPaymentReceipt() {
    const id = this.paymentReceiptInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/paymentReceipt/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printPaymentReceipt(handlePrint: () => void): void {
    handlePrint();
  }

  *closePaymentReceipt(): any {
    yield this.close();
  }

  loadValues(instance: PaymentReceipt) {
    this.idValue = instance.id;
    this.paymentReceiptFiles = instance.paymentReceipt_data
      ? {
          [instance.paymentReceipt_data.name]: {
            name: instance.paymentReceipt_data.name,
            formattedSize: formatBytes(instance.paymentReceipt_data?.size || 0),
            mimetype: instance.paymentReceipt_data?.mimetype,
            id: instance.paymentReceipt_data.name,
            __status__: "original",
          },
        }
      : {};
    this.crcValue = instance.crc;
    this.typeSelectedValue = instance.type;
    this.amountValue = instance.amount;
    this.issueDateTimeValue = instance.issueDateTime
      ? new Date(instance.issueDateTime)
      : null;
    this.statusValue = instance.status;
    this.totalUsesValue = instance.totalUses;
    this.remainingUsesValue = instance.remainingUses;
    this.usedDateValue = instance.usedDate ? new Date(instance.usedDate) : null;
    this.signedEditionSelectedValue = instance.signedEdition
      ? instance.signedEdition
      : null;
    this.usesRows = instance.uses
      ? Object.assign(
          {},
          ...instance.uses.map((x) => {
            const processedRow = getPaymentReceiptUseValues(
              x,
              this.langStore,
              false
            );
            return {
              [`__${x.id}`]: { ...processedRow, __status__: "original" },
            };
          })
        )
      : {};
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadPaymentReceiptById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
